/**
 * State machine for the Bulk Assign Units drawer.
 *
 * Naming follows the schema, not the UI: a PARENT group is what film tenants
 * call a "production" and a CHILD group is a "site". Display wording comes from
 * the label system; nothing in here hardcodes it.
 *
 * Staging is derived, never accumulated. Paste-derived units are recomputed
 * from the current text on every keystroke, so deleting a code from the
 * manifest removes that unit from the batch instead of leaving a ghost that
 * still gets written. Units added from the fleet picker (or prefilled from the
 * table selection) are tracked separately, and an explicit drag/click target
 * beats both.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  parseUnitCodes,
  parseDateRange,
  suggestUnitCode,
  normalizeName,
  scopeLinesToSites,
  isDateLive,
  expandUnitCodeShorthand,
} from '@/lib/unitCodeParser';
import { applyShorthandPicks, pickedShorthandContext, writtenForms as writtenFormsOf } from './shorthandPick';
import {
  PARENT_GROUP_TARGET,
  type BulkAssignGroup,
  type BulkAssignUnit,
  type DateWindow,
  type UnmatchedCode,
} from './types';

export type AssignSource = 'paste' | 'fleet';
export type DateScope = { kind: 'unit' | 'model'; key: string };

interface UseBulkAssignArgs {
  open: boolean;
  units: BulkAssignUnit[];
  parentGroups: BulkAssignGroup[];
  sites: BulkAssignGroup[];
  modelCodes: string[];
  /** Units ticked in the fleet table before the drawer opened. */
  prefillUnitIds: string[];
  autoActivate: boolean;
}

const EMPTY_WINDOW: DateWindow = { start: '', end: '' };

export function useBulkAssign({
  open,
  units,
  parentGroups,
  sites,
  modelCodes,
  prefillUnitIds,
  autoActivate,
}: UseBulkAssignArgs) {
  const [source, setSource] = useState<AssignSource>('paste');
  const [rawText, setRawText] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [duplicates, setDuplicates] = useState(0);
  /** Shorthand (`MED1`) that matches more than one unit, by token: the user picks. */
  const [ambiguous, setAmbiguous] = useState<Record<string, string[]>>({});
  /** The user's pick per ambiguous token (ISS-202), re-applied on every re-parse. */
  const [picks, setPicks] = useState<Record<string, string>>({});
  /** Site scoping read out of the pasted text, per unit id. */
  const [pasteTargets, setPasteTargets] = useState<Record<string, string>>({});
  /** Units added by hand (fleet picker, or the table selection). */
  const [manualIds, setManualIds] = useState<string[]>([]);
  /** A destination the user set explicitly; beats any parsed scoping. */
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  /** Units the user removed — a later keystroke must not resurrect them. */
  const [rejected, setRejected] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [targetGroupId, setTargetGroupId] = useState('');
  const [batch, setBatch] = useState<DateWindow>(EMPTY_WINDOW);
  const [batchFromText, setBatchFromText] = useState(false);
  /**
   * The batch window came from an accepted AI suggestion. Its provenance is
   * tied to the text it was suggested for, so any text edit drops it rather
   * than letting it ride along to different units as if typed by hand.
   */
  const [batchFromAi, setBatchFromAi] = useState(false);
  /**
   * Accepted AI corrections, keyed by the pasted token they correct (or by the
   * code itself when it had no token). Re-applied on every re-parse so an
   * unrelated edit elsewhere in the paste does not drop the accepted unit;
   * dropped once its source token leaves the text.
   */
  const [aiCodes, setAiCodes] = useState<Record<string, string>>({});
  const [modelDates, setModelDates] = useState<Record<string, DateWindow>>({});
  const [unitDates, setUnitDates] = useState<Record<string, DateWindow>>({});
  const [lineDates, setLineDates] = useState<Record<string, DateWindow>>({});
  const [dateScope, setDateScope] = useState<DateScope | null>(null);
  const [setActive, setSetActive] = useState(autoActivate);

  /**
   * Each opening is a fresh session. React only runs state initialisers on
   * mount and this hook's component stays mounted between openings, so without
   * an explicit reset the table's selection never staged and the previous
   * batch leaked into the next one.
   *
   * Done during render rather than in an effect — React's documented way to
   * adjust state when a prop changes. It re-renders immediately without
   * committing the stale pass, so the drawer never paints the old batch.
   */
  const [openedWith, setOpenedWith] = useState(open);
  if (open !== openedWith) {
    setOpenedWith(open);
    if (open) {
      setSource(prefillUnitIds.length > 0 ? 'fleet' : 'paste');
      setRawText('');
      setCodes([]);
      setDuplicates(0);
      setPicks({});
      setPasteTargets({});
      setManualIds(prefillUnitIds);
      setOverrides({});
      setRejected([]);
      setSearch('');
      setTargetGroupId('');
      setBatch(EMPTY_WINDOW);
      setBatchFromText(false);
      setBatchFromAi(false);
      setAiCodes({});
      setModelDates({});
      setUnitDates({});
      setLineDates({});
      setDateScope(null);
      setSetActive(autoActivate);
    }
  }

  const unitsByCode = useMemo(() => {
    const map = new Map<string, BulkAssignUnit>();
    units.forEach((u) => map.set(u.unitCode, u));
    return map;
  }, [units]);

  const unitById = useMemo(() => {
    const map = new Map<string, BulkAssignUnit>();
    units.forEach((u) => map.set(u.id, u));
    return map;
  }, [units]);

  const knownCodes = useMemo(() => units.map((u) => u.unitCode), [units]);

  const writtenForms = useCallback((token: string) => writtenFormsOf(token, modelCodes), [modelCodes]);

  const sitesForTarget = useMemo(
    () => sites.filter((s) => s.parentGroupId === targetGroupId),
    [sites, targetGroupId]
  );

  /* ---------- staging (derived) ---------- */

  const staged = useMemo(() => {
    const rejectedSet = new Set(rejected);
    const result: Record<string, string> = {};
    const add = (id: string, fallback: string) => {
      if (rejectedSet.has(id)) return;
      result[id] = overrides[id] ?? fallback;
    };
    Object.entries(pasteTargets).forEach(([id, target]) => add(id, target));
    manualIds.forEach((id) => add(id, PARENT_GROUP_TARGET));
    return result;
  }, [pasteTargets, manualIds, overrides, rejected]);

  const stagedIds = useMemo(() => Object.keys(staged), [staged]);

  const stage = useCallback((ids: string[], target: string) => {
    if (!ids.length) return;
    setOverrides((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = target;
      });
      return next;
    });
    setManualIds((prev) => [...new Set([...prev, ...ids])]);
    setRejected((prev) => prev.filter((id) => !ids.includes(id)));
  }, []);

  const unstage = useCallback((ids: string[]) => {
    if (!ids.length) return;
    setManualIds((prev) => prev.filter((id) => !ids.includes(id)));
    setOverrides((prev) => {
      const next = { ...prev };
      ids.forEach((id) => delete next[id]);
      return next;
    });
    setRejected((prev) => [...new Set([...prev, ...ids])]);
  }, []);

  const toggleStaged = useCallback(
    (id: string) => {
      if (staged[id]) unstage([id]);
      else stage([id], PARENT_GROUP_TARGET);
    },
    [staged, stage, unstage]
  );

  const idsInTarget = useCallback(
    (target: string) => stagedIds.filter((id) => staged[id] === target),
    [stagedIds, staged]
  );

  /* ---------- dates ---------- */

  /** unit override → line-scoped date → model default → batch. */
  const effectiveDates = useCallback(
    (unit: BulkAssignUnit): DateWindow =>
      unitDates[unit.id] ?? lineDates[unit.id] ?? modelDates[unit.modelCode] ?? batch,
    [unitDates, lineDates, modelDates, batch]
  );

  const setScopedDate = useCallback(
    (kind: 'unit' | 'model', key: string, field: 'start' | 'end', value: string) => {
      // Seed from the window the row is CURRENTLY showing. Seeding from the
      // batch instead silently rewrote the other end whenever the row had
      // inherited its dates from a parsed line or a model override.
      const seed =
        kind === 'unit'
          ? (unitDates[key] ?? lineDates[key] ?? modelDates[unitById.get(key)?.modelCode ?? ''] ?? batch)
          : (modelDates[key] ?? batch);
      const apply = (prev: Record<string, DateWindow>) => ({
        ...prev,
        [key]: { ...(prev[key] ?? seed), [field]: value },
      });
      if (kind === 'unit') setUnitDates(apply);
      else setModelDates(apply);
    },
    [batch, unitDates, lineDates, modelDates, unitById]
  );

  const clearScopedDate = useCallback((kind: 'unit' | 'model', key: string) => {
    const drop = (prev: Record<string, DateWindow>) => {
      const next = { ...prev };
      delete next[key];
      return next;
    };
    if (kind === 'unit') setUnitDates(drop);
    else setModelDates(drop);
    setDateScope(null);
  }, []);

  const toggleDateScope = useCallback((kind: 'unit' | 'model', key: string) => {
    setDateScope((prev) =>
      prev && prev.kind === kind && prev.key === key ? null : { kind, key }
    );
  }, []);

  const setBatchDate = useCallback((field: 'start' | 'end', value: string) => {
    setBatch((prev) => {
      const next = { ...prev, [field]: value };
      // Keep the window ordered without discarding the end the user already
      // chose: push the OTHER end to meet the edited one.
      if (next.start && next.end && next.end < next.start) {
        return field === 'start' ? { start: value, end: value } : { start: value, end: value };
      }
      return next;
    });
    setBatchFromText(false);
    setBatchFromAi(false);
  }, []);

  /* ---------- paste parsing ---------- */

  const applyText = useCallback(
    (rawPaste: string) => {
      // Shorthand ('1re1', '2re16til') becomes the unit's full code before any
      // line is read, so site scoping and dates work on it unchanged. The text box
      // keeps what the user typed; only the parse uses the expanded copy.
      const expansion = expandUnitCodeShorthand(rawPaste, modelCodes, knownCodes);
      const text = expansion.text;
      const parsed = parseUnitCodes(text, modelCodes, { knownCodes });
      // Shorthand left after expansion matched no unit, or more than one: it is
      // listed as unmatched (by its padded token) rather than dropped.
      const shorthandLeft = parsed.partials.map((p) => p.token);
      // Re-apply accepted AI corrections whose source token is still here.
      const flatText = [text, ...shorthandLeft].join(' ').toUpperCase().replace(/[^A-Z0-9]/g, '');
      const liveCorrections = Object.fromEntries(
        Object.entries(aiCodes).filter(([source]) => flatText.includes(source))
      );
      // Picks the user already made for ambiguous shorthand survive the edit (ISS-202).
      const picked = applyShorthandPicks([...parsed.codes, ...shorthandLeft], expansion.ambiguous, picks);
      let nextCodes = picked.codes;
      setPicks(picked.picks);
      setAmbiguous(Object.fromEntries(picked.stillAmbiguous.map((a) => [a.token, a.candidates])));
      Object.entries(liveCorrections).forEach(([source, code]) => {
        // The correction's source is the raw token ('1RE9'); the list holds its padded form ('1RE009').
        const listed = writtenForms(source).find((form) => nextCodes.includes(form));
        nextCodes = listed ? nextCodes.map((c) => (c === listed ? code : c)) : nextCodes;
        if (!nextCodes.includes(code)) nextCodes = [...nextCodes, code];
      });
      nextCodes = [...new Set(nextCodes)];
      setRawText(rawPaste);
      setCodes(nextCodes);
      setDuplicates(parsed.duplicates);
      if (Object.keys(liveCorrections).length !== Object.keys(aiCodes).length) setAiCodes(liveCorrections);

      // Name-drop a group and we select it — but never over the user's pick.
      let groupId = targetGroupId;
      let matchedFromText = false;
      if (!groupId) {
        const flat = normalizeName(text);
        const hit = parentGroups.find((g) => {
          const name = normalizeName(g.name);
          return name.length > 3 && flat.includes(name);
        });
        if (hit) {
          groupId = hit.id;
          matchedFromText = true;
          setTargetGroupId(hit.id);
        }
      }

      const scopedSites = groupId ? sites.filter((s) => s.parentGroupId === groupId) : [];
      const codeToSite = scopeLinesToSites(text, modelCodes, scopedSites);

      // Rebuilt from scratch: a code removed from the text leaves the batch.
      const nextPasteTargets: Record<string, string> = {};
      const nextLineDates: Record<string, DateWindow> = {};
      let standaloneRange: DateWindow | null = null;

      text.split(/\r?\n/).forEach((line) => {
        const lineParse = parseUnitCodes(line, modelCodes, { knownCodes });
        const lineCodes = lineParse.codes;
        const range = parseDateRange(line, modelCodes, undefined, { knownCodes });
        // A date on its own line sets the batch window for everything. A line that
        // names a unit, even as unresolved shorthand ('1re9 8/17'), is not on its own.
        if (range && !lineCodes.length && !lineParse.partials.length && !standaloneRange) standaloneRange = range;

        lineCodes.forEach((code) => {
          const unit = unitsByCode.get(code);
          if (!unit) return;
          if (nextPasteTargets[unit.id]) return; // first mention wins
          nextPasteTargets[unit.id] = codeToSite.get(code) ?? PARENT_GROUP_TARGET;
          if (range) nextLineDates[unit.id] = range;
        });
      });

      Object.values(liveCorrections).forEach((code) => {
        const unit = unitsByCode.get(code);
        if (unit && !nextPasteTargets[unit.id]) nextPasteTargets[unit.id] = PARENT_GROUP_TARGET;
      });
      // A kept pick takes the site and dates written on its line, as when it was picked.
      expansion.ambiguous.forEach(({ token, candidates }) => {
        const code = picked.picks[token];
        const unit = code ? unitsByCode.get(code) : undefined;
        if (!unit || nextPasteTargets[unit.id]) return;
        const { site, range } = pickedShorthandContext({ rawText: rawPaste, picked: code, others: candidates, modelCodes, knownCodes, sites: scopedSites });
        nextPasteTargets[unit.id] = site ?? PARENT_GROUP_TARGET;
        if (range) nextLineDates[unit.id] = range;
      });
      setPasteTargets(nextPasteTargets);
      setLineDates(nextLineDates);
      // Choosing the group from the text invalidates site picks made against
      // whatever was selected before.
      if (matchedFromText) setOverrides({});

      // Only adopt a parsed batch window while the user hasn't set one by hand.
      // An AI window never survives an edit: replace it, or clear it.
      const canAdopt = standaloneRange && (batchFromText || batchFromAi || (!batch.start && !batch.end));
      if (canAdopt && standaloneRange) {
        setBatch(standaloneRange);
        setBatchFromText(true);
      } else if (batchFromAi) {
        setBatch(EMPTY_WINDOW);
      }
      setBatchFromAi(false);
    },
    [modelCodes, knownCodes, writtenForms, parentGroups, sites, unitsByCode, targetGroupId, batch, batchFromText, batchFromAi, aiCodes, picks]
  );

  /**
   * Switching group invalidates every site choice underneath it — but the
   * pasted text may well name sites of the group just chosen, so re-scope
   * against it rather than dumping everything on the parent. Picking the
   * group by hand after pasting is the normal flow when the manifest does
   * not name it.
   */
  const chooseGroup = useCallback(
    (id: string) => {
      setTargetGroupId(id);
      setOverrides({});
      const scopedSites = sites.filter((s) => s.parentGroupId === id);
      const codeToSite = rawText
        ? scopeLinesToSites(expandUnitCodeShorthand(rawText, modelCodes, knownCodes).text, modelCodes, scopedSites)
        : new Map();
      setPasteTargets((prev) =>
        Object.fromEntries(
          Object.keys(prev).map((unitId) => {
            const code = unitById.get(unitId)?.unitCode;
            const site = code ? codeToSite.get(code) : undefined;
            return [unitId, site ?? PARENT_GROUP_TARGET];
          })
        )
      );
    },
    [sites, rawText, modelCodes, knownCodes, unitById]
  );

  /* ---------- unmatched codes ---------- */

  const unmatched = useMemo<UnmatchedCode[]>(() => {
    if (source !== 'paste') return [];
    return codes
      .filter((code) => !unitsByCode.has(code))
      .map((code): UnmatchedCode => {
        const candidates = ambiguous[code];
        if (candidates?.length) return { code, kind: 'ambiguous', suggestion: null, candidates };
        const suggestion = suggestUnitCode(code, knownCodes);
        return { code, kind: suggestion ? 'typo' : 'missing', suggestion };
      });
  }, [codes, unitsByCode, knownCodes, ambiguous, source]);

  const replaceCode = useCallback(
    (oldCode: string, newCode: string) => {
      const normalized = newCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (!normalized) return;
      setCodes((prev) => prev.map((c) => (c === oldCode ? normalized : c)));
      const unit = unitsByCode.get(normalized);
      if (unit) {
        // A pick for ambiguous shorthand ('MED1') keeps the site and dates on its line,
        // and is remembered so a later edit doesn't ask again (ISS-202).
        const others = ambiguous[oldCode];
        if (others?.length) setPicks((prev) => ({ ...prev, [oldCode]: normalized }));
        const { site, range } = others?.length && rawText
          ? pickedShorthandContext({ rawText, picked: normalized, others, modelCodes, knownCodes, sites: sitesForTarget })
          : { site: undefined, range: null };
        setPasteTargets((prev) => ({ ...prev, [unit.id]: prev[unit.id] ?? site ?? PARENT_GROUP_TARGET }));
        if (range) setLineDates((prev) => ({ ...prev, [unit.id]: range }));
        setRejected((prev) => prev.filter((id) => id !== unit.id));
      }
    },
    [unitsByCode, ambiguous, rawText, knownCodes, modelCodes, sitesForTarget]
  );

  /**
   * Stage a unit from an ACCEPTED AI suggestion. `replacing` is the pasted
   * token it corrects: when the parser read that token as an unmatched code it
   * is swapped in place, otherwise the code is added to the batch.
   */
  const adoptCode = useCallback(
    (code: string, replacing?: string) => {
      const unit = unitsByCode.get(code);
      if (!unit) return;
      const typo = replacing?.toUpperCase().replace(/[^A-Z0-9]/g, '');
      setAiCodes((prev) => ({ ...prev, [typo || code]: code }));
      setCodes((prev) => {
        // The pasted token may be listed in its padded form ('2re99' is listed as '2RE099').
        const listed = typo ? writtenForms(typo).find((form) => prev.includes(form)) : undefined;
        const swapped = listed ? prev.map((c) => (c === listed ? code : c)) : prev;
        return swapped.includes(code) ? [...new Set(swapped)] : [...swapped, code];
      });
      setPasteTargets((prev) => ({ ...prev, [unit.id]: prev[unit.id] ?? PARENT_GROUP_TARGET }));
      setRejected((prev) => prev.filter((id) => id !== unit.id));
    },
    [unitsByCode, writtenForms]
  );

  /** Apply a whole window at once (an accepted AI date range). */
  const setBatchWindow = useCallback((window: DateWindow) => {
    setBatch(window.end && window.end < window.start ? { start: window.start, end: window.start } : window);
    setBatchFromText(false);
    setBatchFromAi(true);
  }, []);

  /* ---------- pool + activation ---------- */

  /** The units the list shows: pasted matches, or the filtered fleet. */
  const pool = useMemo(() => {
    if (source === 'paste') {
      const wanted = new Set(codes);
      return units.filter((u) => wanted.has(u.unitCode));
    }
    const q = search.trim().toUpperCase();
    if (!q) return units;
    return units.filter(
      (u) =>
        u.unitCode.includes(q) ||
        u.companyName.toUpperCase().includes(q) ||
        u.modelCode.toUpperCase().includes(q)
    );
  }, [source, codes, units, search]);

  /** Units that this assignment would flip to Active. */
  const activatableCount = useMemo(
    () =>
      stagedIds.filter((id) => {
        const unit = unitById.get(id);
        if (!unit || unit.status === 'active') return false;
        const window = effectiveDates(unit);
        return !window.start || isDateLive(window.start);
      }).length,
    [stagedIds, unitById, effectiveDates]
  );

  /** Earliest future start, for the "statuses unchanged until then" note. */
  const nextFutureStart = useMemo(() => {
    const starts = stagedIds
      .map((id) => {
        const unit = unitById.get(id);
        const window = unit ? effectiveDates(unit) : null;
        return window?.start && !isDateLive(window.start) ? window.start : null;
      })
      .filter((s): s is string => Boolean(s))
      .sort();
    return starts[0] ?? null;
  }, [stagedIds, unitById, effectiveDates]);

  const unchangedCount = useMemo(
    () =>
      stagedIds.filter((id) => {
        const unit = unitById.get(id);
        if (!unit) return false;
        const target = staged[id];
        const destination = target === PARENT_GROUP_TARGET ? targetGroupId : target;
        return unit.primaryGroupId === destination;
      }).length,
    [stagedIds, unitById, staged, targetGroupId]
  );

  /** Empty the batch entirely — including the text that produced it. */
  const reset = useCallback(() => {
    setRawText('');
    setCodes([]);
    setDuplicates(0);
    setPicks({}); // a pick belongs to the text it was made for; Clear starts over
    setPasteTargets({});
    setManualIds([]);
    setOverrides({});
    setRejected([]);
    setTargetGroupId('');
    setBatch(EMPTY_WINDOW);
    setBatchFromText(false);
    setBatchFromAi(false);
    setAiCodes({});
    setModelDates({});
    setUnitDates({});
    setLineDates({});
    setDateScope(null);
  }, []);

  return {
    // source + text
    source,
    setSource,
    rawText,
    applyText,
    codes,
    duplicates,
    search,
    setSearch,
    // staging
    staged,
    stagedIds,
    stage,
    unstage,
    toggleStaged,
    idsInTarget,
    pool,
    reset,
    // destination
    targetGroupId,
    chooseGroup,
    sitesForTarget,
    // dates
    batch,
    setBatchDate,
    setBatchWindow,
    modelDates,
    unitDates,
    lineDates,
    effectiveDates,
    setScopedDate,
    clearScopedDate,
    dateScope,
    toggleDateScope,
    // activation
    setActive,
    setSetActive,
    activatableCount,
    nextFutureStart,
    unchangedCount,
    // unmatched
    unmatched,
    replaceCode,
    adoptCode,
  };
}
